// Pure status-line derivation for a list row (docs/06-ux-rules.md "List" /
// "Status-line derivation"; 11-chassis-plan.md §2 PR 04). One cell per row
// in that table — kept as a single pure function (no component) so it gets
// one test per cell, mirroring `resolveKind`/`deriveResolutionType`'s
// "pure derivation, tested directly" pattern elsewhere in this feature.
import type { FeedbackInboxItem } from 'api/feedbackInbox.types'
import { buildTranslate } from 'locales'

const t = buildTranslate('feedback_inbox.status_line')
const tThread = buildTranslate('feedback_inbox.thread')

// Only "Needs response" is tinted — v83 renders every other status line
// (`.ib-it-status.esc/.ok/.pos/.wait`) in grey70.
export type StatusTone = 'warning' | 'grey'

export interface InboxRowStatus {
  label: string
  tone: StatusTone
  // v83 `raiBadge()`: the label is followed by the RecoveryAI wordmark badge.
  recoveryAiBadge?: boolean
}

// Precedence mirrors the prototype's `ibStatusLine()` (index.html:2266-2274,
// see ANALYSIS.md "Row anatomy"): 👍 wins over everything (a resolved
// conversation can theoretically carry a 👍 rating), then resolved (by
// operator or ai — distinct copy per resolver kind), then escalated, then
// the default "Needs response". `Pending guest reply` (active/abandoned
// conversations) never reaches this function — those rows are excluded from
// the inbox entirely by the membership rule (docs/02-data-model.md §2).
export function inboxRowStatus(item: FeedbackInboxItem): InboxRowStatus {
  if (item.rating === 'up') {
    return { label: t('positive'), tone: 'grey' }
  }

  if (item.status === 'resolved') {
    if (item.resolvedBy?.kind === 'ai') {
      return {
        label: t('resolved_by_prefix'),
        tone: 'grey',
        recoveryAiBadge: true,
      }
    }
    // The optimistic resolve caches `name: null` until the refetch lands;
    // same fallback as the thread pane.
    return {
      label: t('resolved_by', {
        name: item.resolvedBy?.name || tThread('resolved_by_fallback'),
      }),
      tone: 'grey',
    }
  }

  // C2 (AI-1349): a reopen returns even a never-escalated conversation to
  // `escalated`, so the label also needs an escalation row (#2105's
  // `has_escalation`). Absent (older nexus) keeps the old reading.
  const escalatedByAi =
    item.kind === 'recovery_ai' &&
    item.conversationStatus === 'escalated' &&
    item.hasEscalation !== false
  if (escalatedByAi) {
    return { label: t('escalated_by'), tone: 'grey', recoveryAiBadge: true }
  }

  return { label: t('needs_response'), tone: 'warning' }
}
